import { Box, Card, Center, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { ChatHeader } from './ChatHeader';
import { Composer } from './Composer';
import { ConversationList } from './ConversationList';
import { MessageThread } from './MessageThread';
import { usePanelCollapse } from './use-panel-collapse';
import { CollapseRail } from '../CollapseRail';
import type { ChannelCapabilities, InboxAdapter } from './types';

/**
 * Channel-agnostic chat-panel shell. Owns only UI state (active selection,
 * search, sort, draft); all server state + addressing live in the `adapter`,
 * and all feature on/off + slots live in `capabilities`. Each channel route is
 * a thin wrapper: `<ChannelInbox adapter={useXInbox()} capabilities={xCaps} />`.
 */
export function ChannelInbox({
  adapter,
  capabilities,
  initialContactId,
}: {
  adapter: InboxAdapter;
  capabilities: ChannelCapabilities;
  /** Deep-link target: auto-select this contact's conversation once it loads. */
  initialContactId?: number | null;
}) {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [sortBy, setSortBy] = useState<'recent' | 'unread' | 'alpha'>('recent');

  adapter.useRealtime(activeId);
  const list = adapter.useList({ search: debouncedSearch, sortBy });
  const activeItem = activeId != null ? list.items.find((c) => c.id === activeId) : undefined;

  // Switching the list scope (e.g. the Messenger Page) closes the open thread:
  // the previously-selected conversation belongs to a different Page and is no
  // longer in the filtered list.
  const scopeValue = adapter.listScope?.value;
  useEffect(() => {
    setActiveId(null);
  }, [scopeValue]);

  // Deep-link: auto-open the conversation for the passed contactId (from a
  // contact's "Open Messenger" button) once it appears in the loaded list.
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  useEffect(() => {
    if (deepLinkApplied || initialContactId == null || activeId != null) return;
    const match = list.items.find((c) => c.contactId === initialContactId);
    if (match) {
      setActiveId(match.id);
      setDeepLinkApplied(true);
    }
  }, [initialContactId, list.items, activeId, deepLinkApplied]);
  const Icon = capabilities.channelIcon;
  const { leftCollapsed, rightCollapsed, toggleLeft, toggleRight } = usePanelCollapse();

  // Left only collapses while a thread is open (the empty state keeps the list
  // reachable). Both panels stay mounted at width 0 so toggling doesn't refetch
  // and the grid animates the width change.
  const showRightCol = !!capabilities.rightPanel && activeId != null && !rightCollapsed;
  const leftWidth = activeId != null && leftCollapsed ? '0px' : '340px';
  const rightWidth = showRightCol ? '320px' : '0px';

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `${leftWidth} minmax(0, 1fr) ${rightWidth}`,
        gridTemplateRows: 'minmax(0, 1fr)',
        gap: 8,
        height: 'calc(100vh - 100px)',
        overflow: 'hidden',
        transition: 'grid-template-columns 220ms ease',
      }}
    >
      <Card
        p={0}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <ConversationList
          items={list.items}
          isPending={list.isPending}
          activeId={activeId}
          onSelect={setActiveId}
          capabilities={capabilities}
          search={search}
          onSearch={setSearch}
          sortBy={sortBy}
          onSort={setSortBy}
          listScope={adapter.listScope}
        />
      </Card>

      <Box
        className="collapse-pane"
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {activeId != null ? (
          <ChannelThread
            key={activeId}
            adapter={adapter}
            capabilities={capabilities}
            selectionId={activeId}
            unread={activeItem?.unreadCount ?? 0}
          />
        ) : (
          <Center style={{ flex: 1 }}>
            <Stack align="center" gap="xs">
              <Icon size={48} />
              <Text c="dimmed">{capabilities.emptyThreadLabel}</Text>
            </Stack>
          </Center>
        )}
        {activeId != null && (
          <>
            <CollapseRail
              side="left"
              collapsed={leftCollapsed}
              onToggle={toggleLeft}
              label="conversation list"
            />
            {capabilities.rightPanel && (
              <CollapseRail
                side="right"
                collapsed={rightCollapsed}
                onToggle={toggleRight}
                label="contact panel"
              />
            )}
          </>
        )}
      </Box>

      <Box style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        {capabilities.rightPanel && activeId != null
          ? capabilities.rightPanel({
              selectionId: activeId,
              contactId: activeItem?.contactId ?? null,
            })
          : null}
      </Box>
    </Box>
  );
}

function ChannelThread({
  adapter,
  capabilities,
  selectionId,
  unread,
}: {
  adapter: InboxAdapter;
  capabilities: ChannelCapabilities;
  selectionId: number;
  unread: number;
}) {
  const thread = adapter.useThread(selectionId);
  const [draft, setDraft] = useState('');

  // Mark-as-read on open when there's unread.
  useEffect(() => {
    if (unread > 0) adapter.actions.markAsRead(selectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionId, unread]);

  const canFreeText = thread.windowState.canFreeText;

  function send() {
    const text = draft.trim();
    if (!text) return;
    void adapter.actions.sendText(selectionId, text);
    setDraft('');
  }

  function onPickFile(file: File | null) {
    if (file) void adapter.actions.sendMedia?.(selectionId, file);
  }

  return (
    <Card p={0} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {thread.header && (
        <ChatHeader
          header={thread.header}
          windowState={thread.windowState}
          capabilities={capabilities}
          onRefreshProfile={
            adapter.actions.refreshProfile
              ? () => adapter.actions.refreshProfile?.(selectionId)
              : undefined
          }
        />
      )}
      <div style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }} />
      <MessageThread
        messages={thread.messages}
        capabilities={capabilities}
        isPending={thread.isPending}
        hasMore={thread.hasMore}
        loadOlder={thread.loadOlder}
        loadingOlder={thread.isFetchingOlder}
        conversationKey={selectionId}
      />
      <div style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }} />
      {!canFreeText && (
        <Text size="xs" c="dimmed" px="sm" pt="xs">
          The messaging window has closed — the customer must message you again before you can
          reply.
        </Text>
      )}
      <Composer
        capabilities={capabilities}
        selectionId={selectionId}
        draft={draft}
        onDraftChange={setDraft}
        onSend={send}
        onPickFile={capabilities.composer.attachments ? onPickFile : undefined}
        onSendFromLibrary={
          adapter.actions.sendMediaFromLibrary
            ? (item) => adapter.actions.sendMediaFromLibrary?.(selectionId, item.id)
            : undefined
        }
        sendingFromLibrary={adapter.sendingMedia}
        onSendTemplate={
          adapter.actions.sendTemplate
            ? (tpl) => adapter.actions.sendTemplate?.(selectionId, tpl)
            : undefined
        }
        sending={adapter.sendingText}
        sendingMedia={adapter.sendingMedia}
        disabled={!canFreeText}
      />
    </Card>
  );
}
